/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
import { getGenericErrorMessage } from "../../utils/genericErrorMessages.js";
/**
 * @fileoverview Notifications logic module.
 * Handles notifications data retrieval, filtering, sorting, and pagination operations.
 * @module logic/notifications
 */

import { getToken, refreshToken, signOut } from "../../utils/common.js";

/**
 * Creates a user via API. Handles token expiry with refresh and retry.
 * On success calls onSuccess with the created user (including nombreRol).
 * On conflict sets RFC or email error via callbacks.
 *
 * @async
 * @param {Object} notificationData - Notification payload (fileName, s3Path)
 * @param {Object} callbacks
 * @param {Function} callbacks.navigate - React Router navigation function
 * @param {Function} callbacks.onSuccess - Called with created notification
 * @returns {Promise<{ success: boolean }?>} success true when notification was created; undefined on redirect/error
 */
export const getImagePresignedUrl = async (imageData, navigate) => {
  const authToken = getToken();
  if (!authToken) {
    logger.error("Failed to get the token");
    return { success: false };
  }

  const options = {
    method: "POST",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify(imageData),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}upload/presigned-url`,
      options,
    );

    if (response.ok) {
      const responseData = await response.json();
      return { success: true, data: responseData };
    }

    const errorData = await response.json();
    if (errorData?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        if (navigate) {
          navigate("/");
        }
        return {
          success: false,
          message: getGenericErrorMessage("TOKEN_EXPIRED"),
        };
      }
      return await getImagePresignedUrl(imageData, navigate);
    }

    return { success: false, message: errorData?.message };
  } catch (error) {
    logger.error("Error getting image presigned url:", {
      err: sanitizeError(error),
    });
    return { success: false };
  }
};

export const uploadToS3 = async (uploadUrl, file) => {
  await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      "Content-Type": file.type,
    },
    body: file,
  });
};
